import { formatDistanceToNowStrict } from 'date-fns';
import { useTranslation } from 'react-i18next';
import getDateLocale from 'src/utils/getDateLocale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/@/components/ui/dropdown-menu";
import { Button } from 'src/@/components/ui/button';
import Iconify from 'src/components/Iconify';
import { cn } from 'src/@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from 'src/@/components/ui/avatar';

function getInitials(name) {
  if (!name) return '';
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
}

export default function ChatStaffItem({
  chat,
  user,
  onlineUsers,
  onSelectConversation,
  roomsWithNewMessages,
  selectedChat,
  contactList,
  onDelete
}) {
  const { t } = useTranslation();
  const room = chat;
  let other = {};
  chat.people.forEach(person => {
    if (user.id !== person._id) other = person;
  });

  if (!other.firstName) {
    other = { ...other, firstName: 'Deleted', lastName: 'User' };
  }

  const title = (chat.isGroup ? chat.title : `${other.firstName} ${other.lastName}`);
  const initials = getInitials(title);

  let lastMessage = chat.lastMessage?.length > 0 ? chat.lastMessage[0] : null;
  let text = lastMessage?.state === 'removed' ? `🚫 ${t('chat.internal.lastMessage.deleted')}` : lastMessage?.content || '';

  if (lastMessage?.author === user.id && !chat.isGroup && lastMessage?.state !== 'removed')
    text = `${t('chat.header.you')}: ${text}`;
  if (lastMessage?.type === 'file' && lastMessage?.state !== 'removed')
    text = `📎 ${t('chat.internal.lastMessage.sentFile')}`;
  if (lastMessage?.type === 'image' && lastMessage?.state !== 'removed')
    text = `📷 ${t('chat.internal.lastMessage.sentPhoto')}`;
  if (lastMessage?.type === 'audio' && lastMessage?.state !== 'removed')
    text = `🎤 ${t('chat.internal.lastMessage.sentAudio')}`;

  const isSelected = chat._id === selectedChat?._id && !contactList;
  const hasUnread = roomsWithNewMessages.includes(room._id);
  const isMe = lastMessage?.author === user.id;
  const isRead = lastMessage?.read || false;
  const isOnline = onlineUsers?.some(u => u === other._id);
  const isGroupAdmin = chat?.people[0]?._id === user.id && chat.isGroup;

  return (
    <div
      key={room._id}
      className={cn(
        'group relative px-3 py-2 border-b border-gray-100 dark:border-neutral-800 cursor-pointer transition-colors overflow-hidden',
        isSelected
          ? 'bg-violet-50/40 dark:bg-violet-500/5'
          : 'hover:bg-gray-50 dark:hover:bg-neutral-800/50',
        hasUnread && !isSelected && 'bg-violet-50/30 dark:bg-violet-500/5'
      )}
      role="button"
      onClick={onSelectConversation}
    >
      <div className="flex gap-2.5">
        {/* Avatar */}
        <div className="relative flex-shrink-0 mt-0.5">
          <Avatar className="w-9 h-9 rounded-full">
            <AvatarImage src={other.photoURL || other.avatar} alt={title} className="object-cover" />
            <AvatarFallback className="rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-500 dark:text-neutral-400 text-[11px] font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          {isOnline && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white dark:border-neutral-900" />
          )}
          {chat.isGroup && (
            <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-white dark:bg-neutral-900 flex items-center justify-center shadow-sm">
              <Iconify icon="mingcute:group-3-fill" width={10} className="text-gray-400 dark:text-neutral-500" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Name + Time + Actions */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className={cn(
                "text-[13px] truncate",
                hasUnread ? "font-semibold text-gray-900 dark:text-white" : "font-medium text-gray-900 dark:text-white"
              )}>
                {title}
              </span>
              {hasUnread && <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {room?.lastUpdate && (
                <span className={cn(
                  "text-[10px] whitespace-nowrap",
                  hasUnread ? "text-violet-500 font-medium" : "text-gray-400 dark:text-neutral-500"
                )}>
                  {formatDistanceToNowStrict(new Date(room?.lastUpdate), { addSuffix: false, locale: getDateLocale() })}
                </span>
              )}
              {isGroupAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity"
                      size="icon"
                      variant="ghost"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Iconify width={12} icon="mdi:dots-horizontal" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      className="text-xs text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-500/10"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete();
                      }}
                    >
                      <Iconify icon="eva:trash-2-outline" width={14} className="mr-1.5" />
                      {t('chat.internal.deleteGroup')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Row 2: Last message + Read status */}
          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
            {isMe && lastMessage?.state !== 'removed' && (
              <Iconify
                icon="bi:check-all"
                className={cn(
                  "flex-shrink-0 w-3.5 h-3.5",
                  isRead ? "text-sky-500" : "text-gray-400 dark:text-neutral-500"
                )}
              />
            )}
            <span className={cn(
              "text-xs truncate flex-1",
              hasUnread ? "text-gray-700 dark:text-neutral-300 font-medium" : "text-gray-500 dark:text-neutral-400"
            )}>
              {text ? (text.length > 50 ? `${text.slice(0, 50)}…` : text) : <span className="italic text-gray-300 dark:text-neutral-600">{t('chat.internal.noMessages')}</span>}
            </span>

            {hasUnread && (
              <span className="min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-violet-500 px-0.5 text-[8px] font-bold text-white leading-none flex-shrink-0">
                {roomsWithNewMessages.filter(r => room._id === r).length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
